import Button from '@/src/components/atom/Button';
import LucideIcon from '@/src/components/atom/Icons/LucideIcon';
import Input from '@/src/components/atom/Input';
import RadioGroup from '@/src/components/atom/RadioGroup';
import BottomFixedContainer from '@/src/components/molecule/BottomFixedContainer';
import FormContent from '@/src/components/molecule/FormContent';
import MainContentTitle from '@/src/components/molecule/MainContentTitle';
import SectionCard from '@/src/components/molecule/SectionCard';
import { Form, FormField } from '@/src/components/ui/form';
import { BUTTON_CONST } from '@/src/constants/buttons';
import { TOAST_CONST } from '@/src/constants/toast';
import { useToast } from '@/src/hooks/useToast';
import PATH from '@/src/routes/path';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useController, useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { NAV_CONST } from '@/src/constants/nav';
import TextArea from '@/src/components/atom/TextArea';
import { usePrompt } from '@/src/hooks/usePrompt';
import { PutDonationRequestBody } from '@/src/apis/rest/donation.dtos';
import {
  DeleteDonationByIdAPI,
  getDonationByIdAPI,
  putDonationAPI,
} from '@/src/apis/rest/donation';
import DateRangePicker from '@/src/components/atom/DateRangePicker';
import dayjs from 'dayjs';
import { DateRange } from 'react-day-picker';
import ImageUploadInfo from '@/src/components/molecule/ImageUploadInfo';
import ImageUpload from '@/src/components/atom/ImageUpload';
import { Media } from '@/src/models/MeidaDto';
import IconButton from '@/src/components/atom/Button/IconButton';
import Editor from '@/src/components/atom/Editor';
import DonationPreview from '@/src/pages/Donation/components/DonationPreview';
import { webviewDataInjection } from '@/src/helpers/webviewHelper';
import { inAppLinkRegex, urlRegex } from '@/src/utils/regex';
import { Info } from 'lucide-react';
import React, { useEffect, useRef, useState } from 'react';
import useModalState from '@/src/hooks/useModalState';
import AlertDialog from '@/src/components/atom/AlertDialog';
import SectionLabel from '@/src/components/molecule/SectionLabel';
import { formatter } from '@/src/utils/formatter';
import {
  DonationWebviewTemplate,
  donationWebviewMapper,
} from '@/src/helpers/webviewHelper/donation';
import { getContentTemplatesAPI } from '@/src/apis/rest/contentTemplate';
import { COMMON_ALERT_DIALOG_CONST } from '@/src/constants/alertDialog';
import { APIError } from '@/src/models/common/apiError';
import { OPTIONS_CONST } from '@/src/constants/options';
import InfoFormSectionCard from '@/components/organism/InfoFormSectionCard/InfoFormSectionCard';
import useDialog, { DialogType } from '@/src/hooks/useDialog';

type ModalState = 'NONE' | 'DELETE' | 'PRIORITY_ERROR' | 'BANNER_CONNECT_ERROR';

const LIST_PATH = PATH.DONATION_LIST;
const BACK_PATH = PATH.DONATION_DETAIL;

const formSchema = () => {
  return z
    .object({
      id: z.number(),
      startedAt: z.string().min(1),
      endedAt: z.string().min(1),
      goalCredit: z.number(),
      goalCreditString: z.string(),
      isExposed: z.boolean(),
      priorityDisplayYn: z.boolean(),
      sponsorDescription: z.string().optional().nullable(),
      sponsorLink: z.string().regex(urlRegex).optional().nullable(),
      bottomButtonDisplayYn: z.boolean(),
      bottomButtonName: z.string().optional().nullable(),
      bottomButtonLink: z.union([
        z.string().regex(urlRegex).optional().nullable(),
        z.string().regex(inAppLinkRegex).optional().nullable(),
      ]),
      title: z.string().min(1),
      subTitle: z.string().optional().nullable(),
      contents: z.string().min(1),
      sponsorImageId: z.number().optional().nullable(),
      thumbnailImageId: z.number(),
      generateDonationInfos: z.array(
        z.object({
          id: z.number().optional().nullable(),
          contents: z.string().min(1),
        }),
      ),
      generateDonationCautions: z.array(
        z.object({
          id: z.number().optional().nullable(),
          contents: z.string().min(1),
        }),
      ),
      removeInfoIds: z.array(z.number()),
      removeCautionIds: z.array(z.number()),
      allowDuplicateParticipant: z.boolean(),
    })
    .refine(data => {
      if (data.bottomButtonDisplayYn) {
        return data.bottomButtonLink && data.bottomButtonName;
      }

      return true;
    });
};

type FormValues = PutDonationRequestBody & {
  startedDateRange: DateRange;
  thumbnailImage?: Media;
  sponsorImage?: Media;
  goalCreditString?: string;
};
export default function DonationEditPage() {
  // util
  const navigate = useNavigate();
  const { toast } = useToast();
  const { id } = useParams();

  // form
  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema()),
    reValidateMode: 'onSubmit',
  });
  const { field: startedAtField } = useController({
    name: 'startedAt',
    control: form.control,
  });
  const { field: endedAtField } = useController({
    name: 'endedAt',
    control: form.control,
  });
  const { field: bottomButtonNameField } = useController({
    name: 'bottomButtonName',
    control: form.control,
  });
  const { field: bottomButtonLinkField } = useController({
    name: 'bottomButtonLink',
    control: form.control,
  });
  const { field: thumbnailImageField } = useController({
    name: 'thumbnailImage',
    control: form.control,
  });
  const { field: sponsorImageField } = useController({
    name: 'sponsorImage',
    control: form.control,
  });

  // state
  const [defaultData, setDefaultData] = useState('');
  const [isPorceReplace, setIsPorceReplace] = useState(false);
  const { modalState, handleOpenModal, handleCloseModal, handleToggleModal } =
    useModalState<ModalState>();
  const [isSponsorLinkError, setIsSponsorLinkError] = useState(false);
  const [isBottomLinkError, setIsBottomLinkError] = useState(false);
  const {
    Dialog: EditDialog,
    isOpen: isEditOpen,
    openDialog: openEditDialog,
    closeDialog: closeEditDialog,
  } = useDialog({
    type: DialogType.ALERT,
  });
  const formRef = useRef<HTMLFormElement>(null);

  // query
  const { data: detailData } = useQuery({
    queryKey: ['donation-detail', id],
    queryFn: () => getDonationByIdAPI(Number(id)),
    meta: {
      backUrl: BACK_PATH,
      callback: () => {
        navigate(BACK_PATH.replace(':id', String(id)), { replace: true });
      },
    },
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchIntervalInBackground: false,
  });
  const isProceeding =
    dayjs().isAfter(detailData?.startedAt) &&
    dayjs().isBefore(detailData?.endedAt);
  const isEnded = dayjs(detailData?.endedAt).isBefore(dayjs(), 'days');
  const isDeleteDisabled = () => {
    // 진행 중이거나 노출 중 인 경우 삭제 불가
    const isExposed = detailData?.isExposed;
    return isExposed || isProceeding || deleteMutation.isPending;
  };

  const { data: contentTemplateData } = useQuery({
    queryKey: ['content-template', 'DONATION'],
    queryFn: () => {
      return getContentTemplatesAPI({ type: 'DONATION' });
    },
  });
  const contentsMarkup = webviewDataInjection<DonationWebviewTemplate>(
    contentTemplateData?.rows?.[0]?.markup || '',
    donationWebviewMapper({
      ...form.watch(),
      donationInfos: form.watch('generateDonationInfos'),
      donationCautions: form.watch('generateDonationCautions'),
    }),
  );

  const hasOptionalInfo =
    !!detailData?.bottomButtonDisplayYn ||
    !!detailData?.sponsorImage ||
    !!detailData?.sponsorDescription ||
    !!detailData?.sponsorLink ||
    false;

  // mutation
  const putMutation = useMutation({ mutationFn: putDonationAPI });
  const deleteMutation = useMutation({
    mutationFn: DeleteDonationByIdAPI,
  });

  // event
  const handleGoBack = () => {
    navigate(-1);
  };
  const handleDeleteMagazine = () => {
    deleteMutation.mutate(
      { id: Number(id) },
      {
        onSuccess: () => {
          setIsPorceReplace(true);
          toast({
            variant: 'success',
            title: TOAST_CONST.deleteSuccess.title,
            description: TOAST_CONST.deleteSuccess.description,
          });
          setTimeout(() => {
            navigate(LIST_PATH, { replace: true });
          }, 0);
        },
        onError: e => {
          const { error } = e as unknown as APIError;

          if (error === 'THIS_CONTENT_HAVE_BANNER') {
            handleOpenModal('BANNER_CONNECT_ERROR');
          } else {
            toast({
              variant: 'error',
              title: TOAST_CONST.error.title,
              description: e.message,
            });
          }
        },
      },
    );
  };
  const handleSubmit = (values: PutDonationRequestBody) => {
    const newValues = {
      ...values,
      sponsorDescription: values.sponsorDescription || '',
      sponsorLink: values.sponsorLink || '',
      bottomButtonName: values.bottomButtonName || '',
      bottomButtonLink: values.bottomButtonLink || '',
      subTitle: values.subTitle || '',
      sponsorImageId: values.sponsorImageId || null,
    };

    putMutation.mutate(
      { ...newValues },
      {
        onSuccess: () => {
          setIsPorceReplace(true);
          toast({
            variant: 'success',
            title: TOAST_CONST.saveSuccess.title,
            description: TOAST_CONST.saveSuccess.description,
          });

          navigate(BACK_PATH.replace(':id', String(id)));
        },
        onError: e => {
          const { error } = e as unknown as APIError;

          if (error === 'CANNOT_PRIORITY_EXPOSE_OVER_THAN_FIVE') {
            handleOpenModal('PRIORITY_ERROR');
          } else {
            toast({
              variant: 'error',
              title: TOAST_CONST.error.title,
              description: e.message,
            });
          }
        },
      },
    );
  };

  // render
  const startedDateRangeDefaultMessage =
    isProceeding || isEnded ? (
      <p className="flex gap-1">
        <Info size={20} />
        {isEnded
          ? '종료된 컨텐츠는 수정이 불가능합니다.'
          : '진행중인 컨텐츠는 종료일만 수정 가능합니다.'}
      </p>
    ) : undefined;
  const getDateDisabledMatcher = (date: Date) => {
    if (dayjs().isAfter(date, 'D')) return true;

    return false;
  };
  const renderBottomFixedContent = () => {
    return (
      <BottomFixedContainer>
        <div className="flex justify-between">
          <Button
            className="w-max"
            buttonSize="medium"
            buttonColor="outline"
            onClick={handleGoBack}
          >
            <div className="flex items-center gap-x-2">
              <LucideIcon name="ChevronLeft" size={16} />
              {BUTTON_CONST.backToScreen}
            </div>
          </Button>
          <div className="flex gap-3">
            <Button
              disabled={
                defaultData === JSON.stringify(form.watch()) ||
                !form.formState.isValid
              }
              type="button"
              onClick={openEditDialog}
              className="w-40"
              buttonSize="medium"
              isLoading={putMutation.isPending}
            >
              <div className="flex items-center gap-x-2">
                {BUTTON_CONST.save}
              </div>
            </Button>
          </div>
        </div>
      </BottomFixedContainer>
    );
  };

  // effect
  useEffect(() => {
    if (detailData) {
      const resetFormData = {
        ...detailData,
        startedDateRange: {
          from: new Date(detailData?.startedAt),
          to: new Date(detailData?.endedAt),
        },
        sponsorImageId: detailData?.sponsorImage?.id,
        thumbnailImageId: detailData?.thumbnailImage?.id,
        generateDonationInfos: detailData?.donationInfos || [],
        generateDonationCautions: detailData?.donationCautions || [],
        removeInfoIds: [],
        removeCautionIds: [],
        goalCredit: detailData?.goalCredit,
        goalCreditString: formatter.NUM_COMMA_INSTANTLY(detailData?.goalCredit),
      };

      form.reset(resetFormData);
      setDefaultData(JSON.stringify(resetFormData).replaceAll(' ', ''));
    }
  }, [form, detailData]);

  // usePrompt
  usePrompt(
    defaultData !== JSON.stringify(form.watch()).replaceAll(' ', '') &&
      !form.formState.isSubmitted &&
      !isPorceReplace,
  );

  return (
    <>
      <MainContentTitle
        breadcrumbList={[NAV_CONST.donationManagement, NAV_CONST.donationEdit]}
        pageTitle={NAV_CONST.donationEdit}
      />
      {detailData && (
        <Form {...form}>
          <form
            ref={formRef}
            className="relative mb-[100px] grid grid-cols-[1fr_1fr] gap-4"
            onSubmit={form.handleSubmit(handleSubmit)}
          >
            <SectionCard cardTitle="기부하기 기본 정보" className="col-span-2">
              <div className="grid grid-cols-[1fr_auto_1fr] gap-6">
                <div className="flex flex-col gap-6">
                  <FormField
                    control={form.control}
                    name="startedDateRange"
                    render={({ field, fieldState: { error } }) => {
                      return (
                        <FormContent
                          isRequired
                          label="모금 기간"
                          error={error}
                          defaultMessage={startedDateRangeDefaultMessage}
                        >
                          <DateRangePicker
                            {...field}
                            onChange={e => {
                              field.onChange(
                                isProceeding
                                  ? {
                                      ...e,
                                      from: new Date(
                                        dayjs(detailData?.startedAt).format(
                                          'YYYY-MM-DD',
                                        ),
                                      ),
                                    }
                                  : e,
                              );

                              startedAtField.onChange(
                                isProceeding
                                  ? dayjs(detailData?.startedAt).format(
                                      'YYYY-MM-DD',
                                    )
                                  : e?.from
                                    ? dayjs(e?.from).format('YYYY-MM-DD')
                                    : '',
                              );
                              endedAtField.onChange(
                                e?.to ? dayjs(e?.to).format('YYYY-MM-DD') : '',
                              );
                            }}
                            dateDisabledMatcher={getDateDisabledMatcher}
                            triggerProps={{
                              disabled: isEnded,
                            }}
                          />
                        </FormContent>
                      );
                    }}
                  />
                  <FormField
                    control={form.control}
                    name="isExposed"
                    render={({ field, fieldState: { error } }) => {
                      return (
                        <FormContent label="노출 여부" error={error}>
                          <RadioGroup
                            optionList={[
                              { label: OPTIONS_CONST.exposed, value: 'true' },
                              {
                                label: OPTIONS_CONST.notExposed,
                                value: 'false',
                              },
                            ]}
                            rootProps={{
                              value: field.value ? 'true' : 'false',
                              onValueChange: e => field.onChange(e === 'true'),
                            }}
                          />
                        </FormContent>
                      );
                    }}
                  />
                  <FormField
                    control={form.control}
                    name="priorityDisplayYn"
                    render={({ field, fieldState: { error } }) => {
                      return (
                        <FormContent label="우선 노출 여부" error={error}>
                          <RadioGroup
                            optionList={[
                              { label: OPTIONS_CONST.applied, value: 'true' },
                              {
                                label: OPTIONS_CONST.notApplied,
                                value: 'false',
                              },
                            ]}
                            rootProps={{
                              value: field.value ? 'true' : 'false',
                              onValueChange: e => field.onChange(e === 'true'),
                            }}
                          />
                        </FormContent>
                      );
                    }}
                  />

                  <FormField
                    control={form.control}
                    name="thumbnailImageId"
                    render={({ field, fieldState: { error } }) => {
                      return (
                        <FormContent
                          isRequired
                          label="썸네일 이미지"
                          defaultMessage={
                            <ImageUploadInfo widthSize={375} heightSize={375} />
                          }
                          error={error}
                        >
                          <ImageUpload
                            width={128}
                            height={128}
                            widthText={335}
                            heightText={335}
                            maxImageSizeMb={5}
                            imageClassName="object-cover"
                            value={thumbnailImageField?.value?.bufferFullUrl}
                            onChange={e => {
                              field.onChange(e?.id);
                              thumbnailImageField.onChange(e);
                            }}
                            topAccessary={({ handleEdit, handleRemove }) => {
                              return (
                                <>
                                  <div className="flex gap-x-1">
                                    <IconButton
                                      buttonSize="xSmall"
                                      buttonColor="outline"
                                      lucideIconProps={{ name: 'Pencil' }}
                                      onClick={() => {
                                        handleEdit();
                                      }}
                                    />
                                    <IconButton
                                      buttonSize="xSmall"
                                      buttonColor="outline"
                                      lucideIconProps={{ name: 'X' }}
                                      onClick={() => {
                                        handleRemove();
                                        field.onChange(undefined);
                                        thumbnailImageField.onChange(undefined);
                                      }}
                                    />
                                  </div>
                                </>
                              );
                            }}
                          />
                        </FormContent>
                      );
                    }}
                  />
                </div>
                <div className="border-r border-r-gray1" />
                <div className="flex flex-col gap-6">
                  <FormField
                    control={form.control}
                    name="allowDuplicateParticipant"
                    render={({ field, fieldState: { error } }) => {
                      return (
                        <FormContent label="참여 방법" error={error}>
                          <RadioGroup
                            optionList={[
                              {
                                label: OPTIONS_CONST.allowDuplicateParticipant,
                                value: 'true',
                              },
                              {
                                label:
                                  OPTIONS_CONST.notAllowDuplicateParticipant,
                                value: 'false',
                              },
                            ]}
                            rootProps={{
                              value: field.value ? 'true' : 'false',
                              onValueChange: e => field.onChange(e === 'true'),
                              disabled: isProceeding || isEnded,
                            }}
                          />
                        </FormContent>
                      );
                    }}
                  />
                  <SectionLabel
                    labelProps={{
                      className: 'translate-y-2',
                    }}
                    label="참여자 수"
                    value={
                      <Input
                        containerClassName="flex-1"
                        value={formatter.NUM_COMMA(detailData?.participantsCnt)}
                        disabled
                      />
                    }
                  />
                  <SectionLabel
                    labelProps={{
                      className: 'translate-y-2',
                    }}
                    label="모금 크레딧"
                    value={
                      <Input
                        containerClassName="flex-1"
                        value={formatter.NUM_COMMA(
                          detailData?.participantsCredit,
                        )}
                        disabled
                      />
                    }
                  />
                  <FormField
                    control={form.control}
                    name="goalCreditString"
                    render={({ field, fieldState: { error } }) => {
                      return (
                        <FormContent
                          label="목표 크레딧"
                          isRequired
                          error={error}
                        >
                          <Input
                            type="text"
                            onClear={() => {
                              form.setValue('goalCredit', 0);
                              field.onChange(undefined);
                            }}
                            placeholder="목표 크레딧을 입력해주세요"
                            maxLength={17}
                            {...field}
                            value={
                              field.value
                                ? formatter.NUM_COMMA_INSTANTLY(field.value)
                                : undefined
                            }
                            onChange={e => {
                              form.setValue(
                                'goalCredit',
                                formatter.STRING_TO_NUM(e.target.value),
                              );
                              field.onChange(
                                e.target.value
                                  ? formatter.NUM_COMMA_INSTANTLY(
                                      e.target.value,
                                    )
                                  : undefined,
                              );
                            }}
                          />
                        </FormContent>
                      );
                    }}
                  />
                  <SectionLabel
                    labelProps={{
                      className: 'translate-y-2',
                    }}
                    label="등록 일시"
                    value={
                      <Input
                        containerClassName="flex-1"
                        value={dayjs(detailData?.createdAt).format(
                          'YYYY-MM-DD HH:mm:ss',
                        )}
                        disabled
                      />
                    }
                  />
                  <SectionLabel
                    labelProps={{
                      className: 'translate-y-2',
                    }}
                    label="등록 관리자"
                    value={
                      <Input
                        containerClassName="flex-1"
                        value={detailData?.administrator?.name || '-'}
                        disabled
                      />
                    }
                  />
                </div>
              </div>
            </SectionCard>
            <SectionCard
              cardTitle="기부하기 선택 정보"
              className="col-span-2"
              isCollapsible
              defaultOpen={hasOptionalInfo}
            >
              <div className="grid grid-cols-[1fr_auto_1fr] gap-6">
                <div className="flex flex-col gap-6">
                  <FormField
                    control={form.control}
                    name="sponsorImageId"
                    render={({ field, fieldState: { error } }) => {
                      return (
                        <FormContent
                          label="제휴사 이미지"
                          defaultMessage={
                            <ImageUploadInfo widthSize={375} heightSize={375} />
                          }
                          error={error}
                        >
                          <ImageUpload
                            width={128}
                            height={128}
                            widthText={335}
                            heightText={335}
                            maxImageSizeMb={5}
                            imageClassName="object-cover"
                            value={sponsorImageField?.value?.bufferFullUrl}
                            onChange={e => {
                              field.onChange(e?.id);
                              sponsorImageField.onChange(e);
                            }}
                            topAccessary={({ handleEdit, handleRemove }) => {
                              return (
                                <>
                                  <div className="flex gap-x-1">
                                    <IconButton
                                      buttonSize="xSmall"
                                      buttonColor="outline"
                                      lucideIconProps={{ name: 'Pencil' }}
                                      onClick={() => {
                                        handleEdit();
                                      }}
                                    />
                                    <IconButton
                                      buttonSize="xSmall"
                                      buttonColor="outline"
                                      lucideIconProps={{ name: 'X' }}
                                      onClick={() => {
                                        handleRemove();
                                        field.onChange(undefined);
                                        sponsorImageField.onChange(undefined);
                                      }}
                                    />
                                  </div>
                                </>
                              );
                            }}
                          />
                        </FormContent>
                      );
                    }}
                  />
                  <FormField
                    control={form.control}
                    name="sponsorDescription"
                    render={({ field, fieldState: { error } }) => {
                      return (
                        <FormContent label="제휴사 설명" error={error}>
                          <TextArea
                            placeholder="제휴사에 대해 간략한 설명을 적을 수 있어요"
                            className="h-[100px]"
                            {...field}
                          />
                        </FormContent>
                      );
                    }}
                  />
                  <FormField
                    control={form.control}
                    name="sponsorLink"
                    render={({ field, fieldState: { error } }) => {
                      return (
                        <FormContent
                          label="제휴사 홈페이지 링크"
                          error={
                            isSponsorLinkError
                              ? {
                                  type: 'pattern',
                                  message:
                                    'url 형식을 확인해주세요 (https://example.com)',
                                }
                              : undefined
                          }
                        >
                          <Input
                            {...field}
                            onChange={e => {
                              field.onChange(e.target.value || undefined);

                              if (e.target.value) {
                                setIsSponsorLinkError(
                                  !urlRegex.test(e.target.value),
                                );
                              } else {
                                setIsSponsorLinkError(false);
                              }
                            }}
                            onClear={() => {
                              field.onChange('');
                              setIsSponsorLinkError(false);
                              setTimeout(() => {
                                field.onChange(undefined);
                              }, 0);
                            }}
                            placeholder="링크를 입력해주세요 (https://example.com)"
                          />
                        </FormContent>
                      );
                    }}
                  />
                </div>
                <div className="border-r border-r-gray1" />
                <div className="flex flex-col gap-6">
                  <FormField
                    control={form.control}
                    name="bottomButtonDisplayYn"
                    render={({ field, fieldState: { error } }) => {
                      return (
                        <FormContent label="하단 버튼 노출 여부" error={error}>
                          <RadioGroup
                            optionList={[
                              { label: '선택 안함', value: 'false' },
                              { label: '버튼 생성', value: 'true' },
                            ]}
                            rootProps={{
                              value: field.value ? 'true' : 'false',
                              onValueChange: e => {
                                if (e === 'false') {
                                  bottomButtonNameField.onChange('');
                                  bottomButtonLinkField.onChange('');
                                  setTimeout(() => {
                                    bottomButtonNameField.onChange(undefined);
                                    bottomButtonLinkField.onChange(undefined);
                                  }, 0);
                                }
                                field.onChange(e === 'true');
                              },
                            }}
                          />
                        </FormContent>
                      );
                    }}
                  />
                  <FormField
                    control={form.control}
                    name="bottomButtonName"
                    render={({ field, fieldState: { error } }) => {
                      return (
                        <FormContent label="하단 버튼 명" error={error}>
                          <Input
                            maxLength={20}
                            disabled={!form.watch('bottomButtonDisplayYn')}
                            onClear={() => field.onChange(undefined)}
                            placeholder="하단 버튼 이름을 입력해주세요 (최대 20글자)"
                            {...field}
                          />
                        </FormContent>
                      );
                    }}
                  />
                  <FormField
                    control={form.control}
                    name="bottomButtonLink"
                    render={({ field, fieldState: { error } }) => {
                      return (
                        <FormContent
                          label="하단 버튼 링크"
                          error={
                            isBottomLinkError
                              ? {
                                  type: 'pattern',
                                  message:
                                    '하단 버튼 링크 형식을 확인해주세요 (https://example.com 또는 /app/*)',
                                }
                              : undefined
                          }
                        >
                          <Input
                            disabled={!form.watch('bottomButtonDisplayYn')}
                            {...field}
                            onChange={e => {
                              field.onChange(e.target.value || undefined);

                              if (e.target.value) {
                                setIsBottomLinkError(
                                  !urlRegex.test(e.target.value) &&
                                    !inAppLinkRegex.test(e.target.value),
                                );
                              } else {
                                setIsBottomLinkError(false);
                              }
                            }}
                            onClear={() => {
                              field.onChange('');
                              setIsBottomLinkError(false);
                              setTimeout(() => {
                                field.onChange(undefined);
                              }, 0);
                            }}
                            placeholder={
                              '링크를 입력해주세요 (https://example.com 또는 /app/*)'
                            }
                          />
                        </FormContent>
                      );
                    }}
                  />
                </div>
              </div>
            </SectionCard>
            <SectionCard cardTitle="기부하기 내용" className="col-span-2">
              <div className="grid grid-cols-[1fr_auto_1fr] gap-6">
                <div className="col-span-3 flex gap-6">
                  <div className="flex flex-1 flex-col gap-6">
                    <FormField
                      control={form.control}
                      name="title"
                      render={({ field, fieldState: { error } }) => {
                        return (
                          <FormContent isRequired label="제목" error={error}>
                            <Input
                              onClear={() => field.onChange(undefined)}
                              placeholder="제목을 입력해주세요"
                              {...field}
                            />
                          </FormContent>
                        );
                      }}
                    />
                    <FormField
                      control={form.control}
                      name="subTitle"
                      render={({ field, fieldState: { error } }) => {
                        return (
                          <FormContent label="서브 제목" error={error}>
                            <Input
                              onClear={() => field.onChange(undefined)}
                              placeholder="서브 제목을 입력해주세요"
                              {...field}
                            />
                          </FormContent>
                        );
                      }}
                    />
                    <FormField
                      control={form.control}
                      name="contents"
                      render={({ field, fieldState: { error } }) => {
                        return (
                          <FormContent
                            className="col-span-3"
                            label="내용"
                            isRequired
                            error={error}
                          >
                            <div className="flex gap-3">
                              <div className="flex-1">
                                <Editor
                                  placeholder="내용을 입력하세요"
                                  height={650}
                                  value={field.value || ''}
                                  onChange={field.onChange}
                                />
                              </div>
                            </div>
                          </FormContent>
                        );
                      }}
                    />
                  </div>
                  <DonationPreview contentsMarkup={contentsMarkup} />
                </div>
              </div>
            </SectionCard>
            <InfoFormSectionCard
              cardTitle="안내사항 영역"
              form={form}
              field="generateDonationInfos"
              removeField="removeInfoIds"
            />
            <InfoFormSectionCard
              cardTitle="유의사항 영역"
              form={form}
              field="generateDonationCautions"
              removeField="removeCautionIds"
              buttonText="유의사항 추가하기"
            />
            {renderBottomFixedContent()}
          </form>
        </Form>
      )}
      {/* 모달 */}
      {modalState === 'PRIORITY_ERROR' && (
        <AlertDialog
          open={modalState === 'PRIORITY_ERROR'}
          onOpenChange={() => handleToggleModal('PRIORITY_ERROR')}
          okText={BUTTON_CONST.ok}
          onOk={handleCloseModal}
          title="우선 노출 여부 지정 불가"
          cancelButtonProps={{
            className: 'hidden',
          }}
        >
          <p className="whitespace-pre-wrap">
            이미 해당 기부 기간 내 우선노출 지정 개수가 5개 있습니다.
            <br />
            다른 기부 상품을 확인 후 재시도 해주세요.
          </p>
        </AlertDialog>
      )}
      {modalState === 'BANNER_CONNECT_ERROR' && (
        <AlertDialog
          open={modalState === 'BANNER_CONNECT_ERROR'}
          onOpenChange={() => handleToggleModal('BANNER_CONNECT_ERROR')}
          okText={BUTTON_CONST.ok}
          onOk={handleCloseModal}
          title="삭제 불가"
          cancelButtonProps={{
            className: 'hidden',
          }}
        >
          <p className="whitespace-pre-wrap">
            연결된 배너가 있어서 삭제가 불가능합니다.
            <br />
            배너를 확인해주세요.
          </p>
        </AlertDialog>
      )}
      {isEditOpen && (
        <EditDialog
          open={isEditOpen}
          onOk={() => formRef?.current?.requestSubmit()}
          onOpenChange={e => {
            if (e) {
              openEditDialog();
            } else {
              closeEditDialog();
            }
          }}
          title={COMMON_ALERT_DIALOG_CONST.edit}
          isLoading={putMutation.isPending}
        />
      )}
    </>
  );
}
